import {makeAutoObservable, observable} from 'mobx'
import {Lang} from 'yougile-lang'
import {logger, Util} from 'yougile-util'
import {ChatMessage, Hub, MessageTextParams} from 'yougile-model'
import {Chat, ChatInputKeydownData} from '@api/model/chat'
import {Accounts} from '@client/accounts'
import {EventBus} from '@client/event-bus'
import {
  chatEditStateChangedEvent,
  chatForwardStateChangedEvent,
  chatReplyStateChangedEvent
} from '@client/events'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {Backend} from '@common/backend'
import {Debouncer} from '@common/debouncer'
import {FileChooser} from '@common/file-chooser'
import {FilePreview} from '@common/file-preview'
import {ChatMessageFile} from '@components/common/base/chat-message'
import {SmartTextItem} from '@components/common/smart-textedit/types'
import {MessageBox} from '@components/message-box'
import {screencastStore} from '@components/screencast'
import {createUseStore, getChatIdWithKind} from '@containers/hooks'
import {ChatSubscribers} from '@extensions/chat-subscribers'
import {ChatUtils} from '@extensions/chat-utils'
import {globals} from '@extensions/globals-base'
import {MessageUtils} from '@extensions/message-utils'
import {ChatStore} from '../chat-store'
import {CustomEditorRef} from './use-chat-input-custom-editor'

const typingDebouncer = new Debouncer(5000)

interface State {
  item: SmartTextItem | undefined
  hasText: boolean | undefined
  allowSendFiles: boolean
  chosenFiles: File[]
  messageFiles: ChatMessageFile[]
  noTabs: boolean
  hub?: Hub
  messageHtml: string
  inputType: 'classic' | 'balloon'
  autofocus: boolean
  /**
   * Custom placeholder for chat input. Keep it undefined to use default placeholder.
   */
  placeholder?: string
}

interface SavedAttachedFiles {
  messageFiles: ChatMessageFile[]
  chosenFilesData: {
    name: string
    size: number
    type: string
    lastModified: number
    content: string // base64
  }[]
}

interface SavedChatInputState {
  messageToEditTimestamp?: number
  messageToReplyTimestamp?: number
  editingText?: string
}

type PostMessage = {
  message: string
  params?: MessageTextParams
  editedMessageTimestamp?: number
}

export class ChatInputStore {
  state: State = {
    item: undefined,
    hasText: false,
    allowSendFiles: false,
    chosenFiles: [],
    messageFiles: [],
    noTabs: true,
    messageHtml: '',
    inputType: 'balloon',
    autofocus: true,
    placeholder: undefined
  }

  private latestLoadChat: string | undefined
  private prevSaveTyped = ''
  private prevSaveFiles = ''
  private disposeHandlers: (() => void)[] = []
  private isRestoringState = false

  private attachedFilesCache = new Map<string, SavedAttachedFiles>()

  constructor(
    private chatStore: ChatStore,
    autofocus = true
  ) {
    makeAutoObservable(
      this,
      {
        state: observable.shallow
      },
      {autoBind: true}
    )
    this.chatStore = chatStore
    this.state.autofocus = autofocus
    EventBus.on(chatReplyStateChangedEvent, this.saveChatState)
    EventBus.on(chatEditStateChangedEvent, this.editStateChangeHandler)
    EventBus.on(chatForwardStateChangedEvent, this.saveChatState)
    this.disposeHandlers.push(
      Chat.internalWatchChatInputPlaceholder(this.setPlaceholder),
      Chat.internalListenClearChatInput(this.clearTyped)
    )
  }

  public dispose(): void {
    this.disposeHandlers.forEach(handler => handler())
    EventBus.off(chatReplyStateChangedEvent, this.saveChatState)
    EventBus.off(chatEditStateChangedEvent, this.editStateChangeHandler)
    EventBus.off(chatForwardStateChangedEvent, this.saveChatState)
  }

  sendUploadedFile(url: string, hub: Hub): void {
    const message = FilePreview.encodeRelativeUrl(url)
    ChatSubscribers.safeSubscribe(globals.currentUserId, hub)
    this.postMessage({message}, hub)
  }

  onEditorContentUpdate(html: string): void {
    this.state.messageHtml = html
    this.state.hasText = !!html.trim()

    Chat.chatInputValueChange(html)

    this.saveTyped()
    this.saveAttachedFiles()

    if (this.chatStore.state.messageToEdit) {
      this.saveChatState()
    }

    if (this.state.hub && html) {
      Accounts.doTypingAt(this.state.hub.id)
      typingDebouncer.runLatest(() => {
        Accounts.stopTyping()
      })
    }
  }

  startScreencast(): void {
    const hub = this.state.hub
    if (!hub) {
      return
    }

    Chat.startScreencast()

    void screencastStore.startRecording(
      {
        onDoneUpload: (url, hub) => {
          // ChatUtils.openChat(hub)
          const kind = getChatIdWithKind().kind
          let msg = ''
          if (kind === 'task') {
            msg = Lang.msg.screencast.messageWhenUploaded(hub.title)
          } else {
            msg = Lang.msg.screencast.messageWhenUploaded2
          }

          MessageBox.show(msg, 'success')
          setTimeout(() => {
            this.sendUploadedFile(url, hub)
          }, 500)
        },
        onUploadFailed: file => {
          // Добавляем файл в список прикрепленных файлов
          this.onChooseFiles([file])
        }
      },
      hub
    )
  }

  toggleInputType(): void {
    this.state.inputType =
      this.state.inputType === 'classic' ? 'balloon' : 'classic'
  }

  initEditor(item: SmartTextItem): void {
    this.state.item = item
    this.loadAttachedFiles()
    this.loadTyped()
  }

  syncOnNew(): void {
    this.state.hub = this.chatStore.state.hub
    this.loadAttachedFiles()
    this.loadTyped()
    this.syncOnUpdate()
  }

  syncOnUpdate(): void {
    this.state.allowSendFiles =
      !!this.state.hub &&
      (this.chatStore.state.noTabs ||
        PermissionsChecker.checkSendFiles(this.state.hub))
  }

  async onEditorPaste(event: ClipboardEvent): Promise<void> {
    const itemList = event.clipboardData?.items
    if (!itemList) {
      return
    }
    const items = [...Util.iter(itemList)]
    for (const item of items) {
      if (item.kind === 'file') {
        if (!this.state.allowSendFiles) {
          MessageBox.warn(Lang.msg.permissionsForbiddenSendFiles)
          continue
        }
        const file = item.getAsFile()
        if (!file) {
          continue
        }

        this.onChooseFiles([file])
      }
    }
  }

  onEditorKeydown(event: KeyboardEvent): void {
    if (!this.state.hub) {
      return
    }
    const value = this.state.item?.getText().trim()
    // pressed Up to edit last message
    if (
      event.key === 'ArrowUp' &&
      !value &&
      this.state.hub.chat.getMessagesCount() > 0
    ) {
      const lastMsg = this.state.hub.chat.lastMessage({
        userId: globals.currentUserId
      })
      if (!lastMsg) {
        return
      }
      this.chatStore.startEdit(lastMsg)
      event.preventDefault()
      return
    }
    if (event.key === 'Escape') {
      if (this.chatStore.state.messageToEdit) {
        this.chatStore.stopEdit()
        this.clearTyped(true)
      }
      if (this.chatStore.state.messageToReply) {
        this.chatStore.cancelReply()
      }
      return
    }
    if (event.keyCode > 40) {
      Accounts.doTypingAt(this.state.hub?.id)
    }
  }

  postMessage(
    {message, params, editedMessageTimestamp}: PostMessage,
    optionalHub?: Hub
  ): void {
    const hub = optionalHub ?? this.state.hub
    if (!hub) {
      return
    }

    const messageToReply = this.chatStore.state.messageToReply
    this.chatStore.cancelReply()

    ChatUtils.postOrUpdateMessage({
      hub,
      message,
      params,
      messageToReply,
      editedMessageTimestamp
    })

    this.chatStore.updateFirstUnread()
    this.chatStore.autoread()
    this.chatStore.scrollBottom(true)
  }

  clearTyped(clearPayload = false): void {
    this.state.hasText = undefined
    this.state.hub && Backend.removeLocal('chat-typed-' + this.state.hub.id)
    clearPayload && this.clearAttachedFiles()
    clearPayload && this.clearChatState()
    this.state.item?.clear()
    this.state.messageHtml = ''
    Chat.chatInputValueChange('')

    Accounts.stopTyping()
  }

  loadTyped(tryCount = 0): void {
    if (tryCount > 10 || !this.state.hub) {
      return
    }

    this.state.hasText = undefined
    if (this.latestLoadChat !== this.state.hub.id) {
      const typed = Backend.loadLocal('chat-typed-' + this.state.hub.id)
      if (typed) {
        this.state.messageHtml = typed
        Chat.chatInputValueChange(typed)
      } else {
        this.clearTyped()
      }
      this.latestLoadChat = this.state.hub.id
    }

    Accounts.stopTyping()
  }

  saveTyped(): void {
    if (!this.state.hub) {
      return
    }

    const value = this.state.messageHtml
    if (this.state.hub.id + value === this.prevSaveTyped) {
      return
    }
    this.prevSaveTyped = this.state.hub.id + (value || '')
    if (value) {
      Backend.saveLocal('chat-typed-' + this.state.hub.id, value)
    } else {
      this.clearTyped()
    }
  }

  async onEditorSubmit(
    message: string,
    params: MessageTextParams,
    editorRef: CustomEditorRef
  ): Promise<void> {
    Accounts.stopTyping()

    const hub = this.state.hub
    if (!hub) {
      return
    }

    message = message.trim()
    const messageToReply = this.chatStore.state.messageToReply
    const files = this.state.chosenFiles
    const messageFiles = this.state.messageFiles
    const isNews = this.chatStore.isNews
    const allowSendFiles = this.state.allowSendFiles
    const messageToEdit = this.chatStore.state.messageToEdit
    const editedMessageTimestamp = messageToEdit?.timestamp

    if (!message && !files.length && !messageFiles.length) {
      this.chatStore.stopEdit()
      return
    }

    if (files.length && !allowSendFiles) {
      MessageBox.warn(Lang.msg.permissionsForbiddenSendFiles)
      return
    }

    const messageFileLinks = messageFiles.map(
      file =>
        `/root/#file:${file.encoded
          .split('/')
          .map(encodeURIComponent)
          .join('/')}`
    )

    this.clearTyped(true)
    this.chatStore.stopEdit()
    this.chatStore.cancelReply()

    if (
      !isNews &&
      !ChatSubscribers.isSubscribed(globals.currentUserId, hub) &&
      PermissionsChecker.editWhoToNotifyMode(hub) !== 'no'
    ) {
      const commit = !params?.chunks?.length
      ChatSubscribers.subscribe(globals.currentUserId, hub, commit)
    }
    const box = Sync.getCurrentSandbox()

    let html = message
    html = MessageUtils.replaceIdTaskPartialWithOldFormat(box, html)
    html = MessageUtils.replaceIdTaskLinksWithOldFormat(box, html)

    const {filesSendFailed} = await ChatUtils.postOrUpdateMessageWithFiles({
      hub,
      message: html,
      params,
      messageToReply,
      editedMessageTimestamp,
      files,
      messageFileLinks
    })

    const hubChanged = this.state.hub?.id !== hub.id

    if (filesSendFailed) {
      if (hubChanged) {
        Backend.saveLocal('chat-typed-' + hub.id, html)
      } else {
        this.state.messageHtml = message
        this.state.hasText = !!html.trim()
        this.state.chosenFiles = files
        this.chatStore.state.messageToReply = messageToReply
        this.chatStore.state.messageToEdit = messageToEdit

        editorRef.current?.setData(message)

        Chat.chatInputValueChange(message)
        this.state.item?.resetTextTo(message)
        this.saveTyped()
      }
    }

    // User may have selected another chat after a long file upload
    if (!hubChanged) {
      this.chatStore.updateFirstUnread()
      this.chatStore.autoread()
      this.chatStore.scrollBottom(true)
    }
  }

  onChooseFiles(newFiles: File[]): void {
    const files: File[] = this.state.chosenFiles.slice()
    for (const file of newFiles) {
      if (file.size && file.size > Backend.uploadFileLimit * 1024 * 1024) {
        MessageBox.warn(Lang.msg.common.fileSizeWarn(Backend.uploadFileLimit))
        continue
      }
      files.push(file)
    }
    this.state.chosenFiles = files
    void this.saveAttachedFiles()
  }

  onUploadClick = async (mouseEvent: React.MouseEvent): Promise<void> => {
    const files: File[] = []
    await FileChooser.chooseFile(mouseEvent, true, (file: File) => {
      files.push(file)
    })
    this.onChooseFiles(files)
  }

  removeFile(idx: number): void {
    this.state.chosenFiles = this.state.chosenFiles.filter((_, i) => i !== idx)
    void this.saveAttachedFiles()
  }

  setMessageFiles(files: ChatMessageFile[]): void {
    this.state.messageFiles = files
    void this.saveAttachedFiles()
  }

  removeMessageFile(idx: number): void {
    this.state.messageFiles = this.state.messageFiles.filter(
      (_, i) => i !== idx
    )
    void this.saveAttachedFiles()
  }

  setAutoFocus(autofocus = true): void {
    this.state.autofocus = autofocus
  }

  public setPlaceholder(placeholder?: string): void {
    this.state.placeholder = placeholder
  }

  /**
   * Should be called when user presses a key in chat input. Used for the platform extensions api.
   * It uses the ChatInputKeydownData type from the api instead of the KeyboardEvent type to avoid exposing the KeyboardEvent (as a part of internal logic) to the extensions.
   * @param data
   */
  public onKeyDown(data: ChatInputKeydownData): void {
    Chat.chatInputKeydown(data)
  }

  private editStateChangeHandler(data: {messageToEdit?: ChatMessage}) {
    if (data.messageToEdit) {
      this.state.messageHtml = MessageUtils.renderHtmlForEdit(
        data.messageToEdit
      )
    }
    this.saveChatState()
  }

  private async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  private base64ToFile(
    base64: string,
    name: string,
    type: string,
    lastModified: number
  ): File {
    const byteString = atob(base64.split(',')[1])
    const arrayBuffer = new ArrayBuffer(byteString.length)
    const uint8Array = new Uint8Array(arrayBuffer)

    for (let i = 0; i < byteString.length; i++) {
      uint8Array[i] = byteString.charCodeAt(i)
    }

    return new File([arrayBuffer], name, {type, lastModified})
  }

  async saveAttachedFiles(): Promise<void> {
    if (!this.state.hub) {
      return
    }

    const hasFiles =
      this.state.chosenFiles.length > 0 || this.state.messageFiles.length > 0
    const filesKey =
      this.state.hub.id +
      JSON.stringify(this.state.chosenFiles.map(f => f.name)) +
      JSON.stringify(this.state.messageFiles)
    if (filesKey === this.prevSaveFiles) {
      return
    }
    this.prevSaveFiles = filesKey

    if (hasFiles) {
      try {
        const chosenFilesData = await Promise.all(
          this.state.chosenFiles.map(async file => ({
            name: file.name,
            size: file.size,
            type: file.type,
            lastModified: file.lastModified,
            content: await this.fileToBase64(file)
          }))
        )

        const savedFiles: SavedAttachedFiles = {
          messageFiles: this.state.messageFiles,
          chosenFilesData
        }

        this.attachedFilesCache.set(this.state.hub.id, savedFiles)
      } catch (error) {
        logger.warn('Failed to save attached files:', error)
      }
    } else {
      this.attachedFilesCache.delete(this.state.hub.id)
    }
  }

  loadAttachedFiles(): void {
    if (!this.state.hub || this.latestLoadChat === this.state.hub.id) {
      return
    }

    const savedFiles = this.attachedFilesCache.get(this.state.hub.id)
    if (!savedFiles) {
      this.clearAttachedFiles()
      return
    }

    try {
      this.state.messageFiles = savedFiles.messageFiles || []
      const restoredChosenFiles =
        savedFiles.chosenFilesData?.map(fileData =>
          this.base64ToFile(
            fileData.content,
            fileData.name,
            fileData.type,
            fileData.lastModified
          )
        ) || []
      this.state.chosenFiles = restoredChosenFiles
    } catch (error) {
      logger.warn('Failed to load attached files:', error)
      this.clearAttachedFiles()
    }
  }

  private clearAttachedFiles(): void {
    this.state.chosenFiles = []
    this.state.messageFiles = []
    if (this.state.hub) {
      this.attachedFilesCache.delete(this.state.hub.id)
    }
  }

  saveChatState(): void {
    if (!this.state.hub) {
      return
    }

    const messageToEdit = this.chatStore.state.messageToEdit
    const messageToReply = this.chatStore.state.messageToReply

    if (!messageToEdit && !messageToReply) {
      Backend.removeLocal('chat-state-' + this.state.hub.id)
      return
    }

    const chatState: SavedChatInputState = {
      messageToEditTimestamp: messageToEdit?.timestamp,
      messageToReplyTimestamp: messageToReply?.timestamp,
      editingText: messageToEdit ? this.state.messageHtml : undefined
    }

    Backend.saveLocal(
      'chat-state-' + this.state.hub.id,
      JSON.stringify(chatState)
    )
  }

  loadChatState(): void {
    if (!this.state.hub) {
      return
    }

    const savedState = Backend.loadLocal('chat-state-' + this.state.hub.id)
    if (!savedState) {
      this.chatStore.state.messageToEdit = undefined
      this.chatStore.state.messageToReply = undefined
      return
    }

    const loadMessage = (timestamp?: number) =>
      timestamp ? this.state.hub?.chat.messages.get(timestamp) : undefined

    try {
      const chatState: SavedChatInputState = JSON.parse(savedState)

      if (chatState.editingText) {
        this.state.messageHtml = chatState.editingText
      }

      this.chatStore.state.messageToEdit = loadMessage(
        chatState.messageToEditTimestamp
      )
      this.chatStore.state.messageToReply = loadMessage(
        chatState.messageToReplyTimestamp
      )
    } catch (error) {
      logger.warn('Failed to load chat state:', error)
      this.chatStore.state.messageToEdit = undefined
      this.chatStore.state.messageToReply = undefined
    }
  }

  private clearChatState(): void {
    if (this.state.hub) {
      Backend.removeLocal('chat-state-' + this.state.hub.id)
    }
  }
}

export const [ChatInputContext, useChatInputStore] =
  createUseStore(ChatInputStore)
